import React, { JSX, useMemo } from 'react';
import DropdownMenu from 'components/DropdownMenu';
import { SortOrder } from 'types';
import classNames from 'classnames';
import useIsMobileDevice from 'hooks/UseIsMobileDevice';
import { CheckIcon, FunnelSimpleIcon, SortAscendingIcon, SortDescendingIcon } from '@phosphor-icons/react';
import { useTranslation } from 'react-i18next';

interface Props<T> {
  fields: Array<[keyof T, string]>;
  sortBy?: keyof T;
  sortOrder: SortOrder;
  onSortBy: (sortBy: keyof T) => void;
  onSortOrder: (sortOrder: SortOrder) => void;
  asIcon: boolean;
  className?: string;
}

/**
 * Filter button that display the current sort field and order and the option to update them
 */
function MobileFilterButton<T>({ asIcon, fields, sortBy, onSortBy, sortOrder, onSortOrder, className }: Props<T>): JSX.Element {
  const isMobileDevice = useIsMobileDevice();
  const { t } = useTranslation();

  // get the current sort field
  const currentSortField = fields.find(field => field[0] === sortBy);

  const sortFields = useMemo(() => {
    return fields.map(field => {
      return {
        element: <SortElement defaultSortBy={field} sortBy={sortBy} sortOrder={sortOrder} />,
        onClick: () => onSortBy(field[0]),
      };
    });
  }, [fields, onSortBy, sortBy, sortOrder]);

  return (
    <DropdownMenu
      ulClassName='min-w-[150px]'
      paddingSize='xs'
      buttonClassName={className}
      menuItems={[
        [
          // This header element should only be visible on mobile
          // as it makes no sense to have it visible on web as by the line
          // separator there is a clear separation between them on web
          {
            element: 'Sort by',
            isHeader: true,
            isVisible: isMobileDevice === true,
          },
          ...sortFields,
        ],
        [
          // This header element should only be visible on mobile
          // as it makes no sense to have it visible on web as by the line
          // separator there is a clear separation between them on web
          {
            element: 'Order by',
            isHeader: true,
            isVisible: isMobileDevice === true,
          },
          {
            element: <SortElement defaultSortOrder={SortOrder.Ascending} sortOrder={sortOrder} />,
            onClick: () => onSortOrder(SortOrder.Ascending),
          },
          {
            element: <SortElement defaultSortOrder={SortOrder.Descending} sortOrder={sortOrder} />,
            onClick: () => onSortOrder(SortOrder.Descending),
          },
        ],
      ]}
    >
      <button
        type='button'
        className={classNames({
          'flex items-center justify-between gap-x-1 w-full': !asIcon,
          'p-1': asIcon,
        })}
      >
        {!asIcon ? (
          <>
            <p>
              {t('sort-by', 'Sort by')}: <span className='text-primary truncate'>{currentSortField?.[1]}</span>
            </p>{' '}
            {sortOrder === SortOrder.Ascending ? <SortAscendingIcon /> : <SortDescendingIcon />}
          </>
        ) : (
          <FunnelSimpleIcon />
        )}
      </button>
    </DropdownMenu>
  );
}

/**
 * Small helper function to position the element in the dropdown
 */
function SortElement<T>({
  defaultSortBy,
  sortBy,
  defaultSortOrder,
  sortOrder,
}: {
  defaultSortBy?: [keyof T, string];
  sortBy?: keyof T;
  defaultSortOrder?: SortOrder;
  sortOrder?: SortOrder;
}): JSX.Element {
  const isMobileDevice = useIsMobileDevice();

  const className = 'flex items-center justify-center md:justify-between gap-x-2';

  if (defaultSortBy) {
    const friendlyName = defaultSortBy[1].charAt(0).toUpperCase() + defaultSortBy[1].slice(1);

    return (
      <div className={className}>
        <span className={classNames({ 'font-semibold': sortBy === defaultSortBy[0] })}>{friendlyName}</span>
        {sortBy === defaultSortBy[0] && !isMobileDevice && <CheckIcon className='text-primary' />}
      </div>
    );
  }

  if (defaultSortOrder) {
    const friendlyName = defaultSortOrder.charAt(0).toUpperCase() + defaultSortOrder.slice(1);

    return (
      <div className={className}>
        <span className={classNames({ 'font-semibold': sortOrder === defaultSortOrder })}>{friendlyName}</span>
        {sortOrder === defaultSortOrder && !isMobileDevice && <CheckIcon className='text-primary' />}
      </div>
    );
  }

  return <></>;
}

export default MobileFilterButton;
